import React from "react";
import { useNavigate } from "react-router-dom";

import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

import { CAURL } from "../../../../routes/ca_routes/CAURL";
import PaymentRequestManager from "../models/client_payments_model/PaymentRequestManager";

import PageHeader from "../../../../components/reusable/management/PageHeader";
import StatsGrid from "../../../../components/reusable/management/StatsGrid";
import DynamicTable from "../../../../components/reusable/DynamicTable";
import BaseIcon from "../../../../components/svg_icons/BaseIcon";


import "../../../../css/pages/ca_pages/finances/ClientPaymentsPage.css";

const COLORS = {
    paid: "#10B981",
    pending: "#F59E0B",
    overdue: "#EF4444",
    cancelled: "#94A3B8",
    primary: "#6366F1",
};

/*
 * ============================================================================
 * HELPERS
 * ============================================================================
 */

const formatCurrency = (value) => {
    const amount = Number(value || 0);

    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (date) => {
    if (!date) { return "-"; }
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) { return "-"; }

    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(parsedDate);
};

/*
 * ============================================================================
 * PAGE
 * ============================================================================
 */

const ClientPaymentsPage = () => {
    const navigate = useNavigate();

    return (
        <PaymentRequestManager
            loadOverview={true}
            loadPlatformOverview={true}
            loadRequests={true}
        >
            {({
                paymentOverview,
                platformOverview,
                paymentRequests,

                loadingOverview,
                loadingPlatformOverview,
                loadingPaymentRequests,

                openCreate,
                openExisting,
                deletePaymentRequest,
            }) => {
                /*
                * ==================================================================
                * DASHBOARD DATA
                * ==================================================================
                */

                const overview = paymentOverview;

                /*
                * platformOverview is intentionally available here.
                *
                * We are not assuming any field names from that endpoint until
                * we know its exact response structure.
                */

                /*
                * ==================================================================
                * PAYMENT STATS
                * ==================================================================
                */

                const paymentStats = overview
                ? [
                    {
                        title: "Total Requested",
                        value: overview.total_requests,
                        icon: "receipt",
                        color: "primary",
                    },
                    {
                        title: "Active",
                        value: overview.active_requests,
                        icon: "clock",
                        color: "warning",
                    },
                    {
                        title: "Paid",
                        value: overview.paid_requests,
                        icon: "check",
                        color: "success",
                    },
                    {
                        title: "Overdue",
                        value: overview.overdue_requests,
                        icon: "circleAlert",
                        color: "danger",
                    },
                ] : [];

                /*
                * ==================================================================
                * STATUS DATA
                * ==================================================================
                */

                const statusData = overview
                ? [
                        {
                            name: "Paid",
                            value: overview.paid_requests || 0,
                            color: COLORS.paid,
                        },
                        {
                            name: "Pending",
                            value: overview.pending_requests || 0,
                            color: COLORS.pending,
                        },
                        {
                            name: "Overdue",
                            value: overview.overdue_requests || 0,
                            color: COLORS.overdue,
                        },
                        {
                            name: "Cancelled",
                            value: overview.cancelled_requests || 0,
                            color: COLORS.cancelled,
                        },
                    ].filter((item) => item.value > 0)
                : [];

                /*
                * ==================================================================
                * AMOUNT DATA
                * ==================================================================
                */

                const amountData = overview
                    ? [
                            {
                                name: "Requested",
                                amount: Number(overview.total_amount_requested || 0),
                            },
                            {
                                name: "Collected",
                                amount: Number(overview.total_amount_collected || 0),
                            },
                            {
                                name: "Outstanding",
                                amount: Number(overview.outstanding_amount || 0),
                            },
                        ]
                    : [];

                /*
                * ==================================================================
                * RECENT PAYMENT REQUESTS
                * ==================================================================
                *
                * getAllPaymentRequests() returns all requests.
                *
                * The dashboard only shows the latest 5.
                */

                const recentPayments = Array.isArray(paymentRequests)
                    ? [...paymentRequests]
                        .sort(
                            (a, b) =>
                            new Date(b.created_at || 0) - new Date(a.created_at || 0),
                        )
                        .slice(0, 5)
                    : [];

                /*
                * ==================================================================
                * TABLE COLUMNS
                * ==================================================================
                */

                const requestsColumn = [
                    { key: "__sn", label: "Sr. No.", sortable: false, },
                    { key: "client_name", label: "Client", sortable: true, },
                    { key: "title", label: "Request Title", sortable: true, },
                    {
                        key: "amount", label: "Amount", sortable: true,
                        render: (value, row) => `${row.currency || "INR"} ${value}`,
                    },
                    { key: "status", label: "Status", sortable: true, },
                    {
                        key: "due_date", label: "Due Date", sortable: true,
                        render: (value) => formatDate(value),
                    },
                ];

                /*
                * ==================================================================
                * TABLE ACTIONS
                * ==================================================================
                */

                const actions = [
                    {
                        icon: <BaseIcon name="eyeOpen" size={20} />,
                        title: "View",
                        onClick: (row) => { openExisting(row); },
                    },
                    // {
                    //   icon: <BaseIcon name="edit" size={20} />,
                    //   title: "Edit",
                    //   onClick: (row) => { openExisting(row); },
                    // },
                    {
                        icon: <BaseIcon name="trash" size={20} />,
                        title: "Delete",
                        buttonType: "delete",
                        onClick: (row) => { deletePaymentRequest(row); },
                    },
                ];

                /*
                * ==================================================================
                * LOADING
                * ==================================================================
                */

                if (loadingOverview || loadingPlatformOverview) {
                    return (
                        <div className="payment-dashboard-loading">
                            <div className="dashboard-loader" />
                            <p>Loading payment dashboard...</p>
                        </div>
                    );
                }

                /*
                * ==================================================================
                * PAGE
                * ==================================================================
                */

                return (
                    <div className="client-payments-page">
                        {/* ============================================================
                            HEADER
                        ============================================================ */}

                        <PageHeader
                            title="Client Payments"
                            subtitle="Track client payments, collection performance and outstanding balances."
                            actions={[
                                {
                                    label: "Request Payment",
                                    icon: "+",
                                    onClick: openCreate,
                                    variant: "primary",
                                },
                            ]}
                        />

                        {/* ============================================================
                            KPI
                        ============================================================ */}

                        <StatsGrid cards={paymentStats} />

                        {/* ============================================================
                            MONEY SUMMARY
                        ============================================================ */}

                        <section className="payment-money-summary">
                            <div className="money-summary-card">
                                <span>Total Requested</span>

                                <strong>
                                    {formatCurrency(overview?.total_amount_requested)}
                                </strong>

                                <small>Across all payment requests</small>
                            </div>

                            <div className="money-summary-card collected">
                                <span>Collected</span>

                                <strong>
                                    {formatCurrency(overview?.total_amount_collected)}
                                </strong>

                                <small className="positive">
                                    {overview?.collection_rate || 0}% collection rate
                                </small>
                            </div>

                            <div className="money-summary-card outstanding">
                                <span>Outstanding</span>

                                <strong>{formatCurrency(overview?.outstanding_amount)}</strong>

                                <small className="negative">Amount still to collect</small>
                            </div>

                            <div className="money-summary-card reminders">
                                <span>Reminders Sent</span>

                                <strong>{overview?.reminders_sent || 0}</strong>

                                <small>
                                    {overview?.reminders_failed || 0} failed deliveries
                                </small>
                            </div>
                        </section>

                        {/* ============================================================
                            CHARTS
                        ============================================================ */}

                        <section className="payment-charts-grid">
                        {/* COLLECTION */}

                        <div className="payment-chart-card large">
                            <div className="chart-header">
                            <div>
                                <h3>Collection Overview</h3>

                                <p>Requested vs collected amount</p>
                            </div>
                            </div>

                            <div className="chart-container">
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart
                                data={amountData}
                                margin={{
                                    top: 10,
                                    right: 10,
                                    left: 0,
                                    bottom: 10,
                                }}
                                >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={false}
                                    stroke="#E2E8F0"
                                />

                                <XAxis dataKey="name" axisLine={false} tickLine={false} />

                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(value) => `₹${value / 1000}k`}
                                />

                                <Tooltip formatter={(value) => formatCurrency(value)} />

                                <Bar
                                    dataKey="amount"
                                    radius={[8, 8, 0, 0]}
                                    fill={COLORS.primary}
                                    barSize={55}
                                />
                                </BarChart>
                            </ResponsiveContainer>
                            </div>
                        </div>

                        {/* STATUS */}

                        <div className="payment-chart-card">
                            <div className="chart-header">
                            <div>
                                <h3>Request Status</h3>

                                <p>Payment request distribution</p>
                            </div>
                            </div>

                            <div className="pie-chart-wrapper">
                            <ResponsiveContainer width="100%" height={270}>
                                <PieChart>
                                <Pie
                                    data={statusData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={4}
                                >
                                    {statusData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>

                                <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>

                            <div className="pie-center">
                                <strong>{overview?.total_requests || 0}</strong>

                                <span>Requests</span>
                            </div>
                            </div>

                            <div className="status-legend">
                            {statusData.map((item) => (
                                <div className="status-item" key={item.name}>
                                <span
                                    className="status-dot"
                                    style={{
                                    background: item.color,
                                    }}
                                />

                                <span>{item.name}</span>

                                <strong>{item.value}</strong>
                                </div>
                            ))}
                            </div>
                        </div>
                        </section>

                        {/* ============================================================
                            RECENT PAYMENT REQUESTS
                        ============================================================ */}

                        <section className="recent-payments-card">
                            <div className="section-header">
                                <div>
                                    <h3>Recent Payment Requests</h3>
                                    <p>Latest requests sent to your clients</p>
                                </div>

                                <button
                                    className="view-all-button"
                                    onClick={() =>
                                        navigate(
                                            CAURL.CA_FINANCE_SHOW_ALL_CLIENT_PAYMENT_REQUESTS_PAGE,
                                        )
                                    }
                                >
                                    View all
                                </button>
                            </div>

                            <div className="payment-table-wrapper">
                                {loadingPaymentRequests ? (
                                    <div className="payment-dashboard-loading">
                                        <div className="dashboard-loader" />
                                        <p>Loading payment requests...</p>
                                    </div>
                                ) : recentPayments.length === 0 ? (
                                    <table className="payment-table">
                                        <thead>
                                            <tr>
                                                <th>Client</th>
                                                <th>Request Title</th>
                                                <th>Amount</th>
                                                <th>Due date</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr>
                                                <td colSpan="5" className="empty-payments">
                                                    No payment requests yet.
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                ) : (
                                    <DynamicTable
                                        columns  = { requestsColumn }
                                        data     = { recentPayments }
                                        onAction = { actions }
                                    />
                                )}
                            </div>
                        </section>
                    </div>
                );
            }}
        </PaymentRequestManager>
    );
};

export default ClientPaymentsPage;
